export class TimeoutWrapper {
    timeout;
    fn;
    constructor() {
        this.timeout = null;
        this.fn = null;
    }
    clear() {
        if (this.timeout) {
            clearTimeout(this.timeout);
            this.timeout = null;
        }
    }
    /**
     * Runs the underlying function if the timeout has not yet expired
     */
    finishEarly() {
        if (this.timeout && this.fn) {
            this.fn();
            this.clear();
        }
    }
    /**
     * Sets the timeout function and time, if it has not already been set
     * @param fn
     * @param time
     */
    set(fn, time) {
        if (this.timeout) {
            throw new Error("Timeout already set!");
        }
        this.fn = fn;
        this.clear();
        this.timeout = setTimeout(() => {
            if (!this.fn) {
                throw new Error("Timeout function should not be unset while running!");
            }
            this.fn();
            this.timeout = null;
        }, time);
    }
    /**
     * Forces a set of the timeout function and time even if it has already been set
     * @param fn
     * @param time
     */
    forceSet(fn, time) {
        this.clear();
        this.set(fn, time);
    }
}
export class IntervalWrapper {
    interval;
    intervalTime;
    waitForCompletion = false;
    counter;
    immediate;
    fnRunning;
    fn;
    exponentialBackoff;
    constructor(options = {}) {
        this.interval = null;
        this.waitForCompletion = options.waitForCompletion ?? false;
        this.exponentialBackoff = options.exponentialBackoff;
        this.immediate = options.immediate ?? false;
        this.fnRunning = false;
        this.counter = 0;
    }
    clear() {
        if (this.interval) {
            clearTimeout(this.interval);
            this.interval = null;
        }
    }
    resetCounter() {
        this.counter = 0;
    }
    resetExponentialBackoff() {
        if (!this.exponentialBackoff) {
            throw new Error("Exponential backoff not set!");
        }
        this.changeTime(this.exponentialBackoff.min);
    }
    changeTimeForExponentialBackoff() {
        if (this.exponentialBackoff) {
            let newTime = this.counter * this.counter * this.exponentialBackoff.min;
            if (newTime < this.exponentialBackoff.min) {
                newTime = this.exponentialBackoff.min;
            }
            if (newTime > this.exponentialBackoff.max) {
                newTime = this.exponentialBackoff.max;
            }
            this.changeTime(newTime);
        }
    }
    /**
     * Wraps the function to wait for completion and indicate when its running
     * @param fn
     * @param options
     */
    prepFunction(fn, options = {}) {
        if (!options.noWrap) {
            const fnWrap = async () => {
                this.fnRunning = true;
                await Promise.resolve(fn());
                this.fnRunning = false;
            };
            this.fn = fnWrap;
        }
        else {
            this.fn = fn;
        }
    }
    /**
     * Sets up an interval that will wait for the prior function to complete before running it again
     */
    setupWaitForCompletionFn() {
        this.interval = setInterval(async () => {
            if (!this.fn) {
                this.clear();
                throw new Error("Interval function should not be unset while running!");
            }
            if (!this.fnRunning) {
                this.counter += 1;
                await this.fn();
                this.changeTimeForExponentialBackoff();
            }
        }, this.intervalTime);
    }
    /**
     * Sets up an interval that will run without waiting for completion of the prior
     */
    setupNormalFn() {
        this.interval = setInterval(() => {
            if (!this.fn) {
                this.clear();
                throw new Error("Interval function should not be unset while running!");
            }
            this.counter += 1;
            this.fn();
            this.changeTimeForExponentialBackoff();
        }, this.intervalTime);
    }
    /**
     * Sets the interval for the given function and time, if it has not already been set
     * @param fn
     * @param time
     */
    set(fn, time, options = {}) {
        this.fnRunning = false;
        if (this.interval) {
            throw new Error("Interval already set!");
        }
        this.clear();
        this.intervalTime = time;
        this.prepFunction(fn, options);
        if (this.fn && this.immediate) {
            this.fn();
            this.immediate = false;
        }
        if (this.waitForCompletion) {
            this.setupWaitForCompletionFn();
        }
        else {
            this.setupNormalFn();
        }
    }
    /**
     * Change the interval time
     * @param time
     */
    changeTime(time) {
        if (!this.fn) {
            throw new Error("Interval function must be set before changing time!");
        }
        this.clear();
        // Make sure nowrap is set so we don't cause recursive wrapping of the original function
        this.set(this.fn, time, { noWrap: true });
    }
    /**
     * Forces a set of the interval function and time even if it has already been set
     * @param fn
     * @param time
     */
    forceSet(fn, time) {
        this.clear();
        this.set(fn, time);
    }
}
function getDefaultIntervalScheduleOptions() {
    return {
        allowOverlappingEventRuns: true
    };
}
;
/**
 * One or more interval functions can run off the same
 * base interval function. When the base interval is stopped
 * all derived functions are also stopped.
 */
export class SharedInterval {
    wrapper;
    schedule;
    baseInterval;
    constructor() {
        this.wrapper = new IntervalWrapper();
        this.schedule = [];
        this.baseInterval = null;
    }
    /**
     * Adds a new scheduled event to the interval which will be run based on the time provided
     * @param fn
     * @param time
     * @param options - allowOverlappingEventRuns: default true
     */
    add(fn, time, { allowOverlappingEventRuns } = getDefaultIntervalScheduleOptions()) {
        const newSchedule = {
            time,
            fn,
            fnInstancesRunning: 0,
            countdown: time,
            allowOverlappingEventRuns
        };
        this.schedule.push(newSchedule);
    }
    /**
     * Checks whether the scheduled event can run based on the elapsed time
     * and any rules the schedule has (e.g. allowOverlappingEventRuns)
     * @param schedule
     */
    isEventAllowedToRun(schedule) {
        if (schedule.countdown > 0) {
            return false;
        }
        if (!schedule.allowOverlappingEventRuns) {
            return true;
        }
        return schedule.fnInstancesRunning === 0;
    }
    /**
     * Updates the instance counters and resets the countdown
     * @param schedule
     */
    runScheduledEvent(schedule) {
        schedule.fnInstancesRunning += 1;
        Promise.resolve(schedule.fn()).then(() => {
            schedule.fnInstancesRunning -= 1;
        });
        schedule.countdown = schedule.time;
    }
    /**
     * Checks what events should be run and runs them
     * @param elapsedTime
     */
    async checkRunEvents(elapsedTime) {
        for (let i = 0; i < this.schedule.length; i++) {
            const schedule = this.schedule[i];
            schedule.countdown -= elapsedTime;
            if (this.isEventAllowedToRun(schedule)) {
                this.runScheduledEvent(schedule);
            }
        }
    }
    start(baseInterval) {
        this.baseInterval = baseInterval;
        this.wrapper.set(() => {
            this.checkRunEvents(this.baseInterval || 0);
        }, this.baseInterval);
    }
    clear() {
        this.wrapper.clear();
        this.baseInterval = null;
        this.schedule = [];
    }
}
//# sourceMappingURL=timeouts.js.map